import React, { useState, useEffect, useCallback } from "react";
import { CircularLoader, Button, Tooltip } from "@dhis2/ui";
import axios from "axios";

const DashboardRefresh = ({
  reportId,
  accessToken,
  groupId,
  datasetId,
  refreshDashboard,
}) => {
  const [dateLoaded, setDateLoaded] = useState(false);
  const [lastRefreshDate, setLastRefreshDate] = useState(null);
  const [lastRefreshTimestamp, setLastRefreshTimestamp] = useState(null);

  const waitForRefreshCompletion = useCallback(
    async (onDone, interval = 5000, maxAttempts = 20) => {
      let attempts = 0;
      let completed = false;
      setDateLoaded(false);
      while (attempts < maxAttempts && !completed) {
        try {
          const response = await fetch(
            `https://api.powerbi.com/v1.0/myorg/groups/${groupId}/datasets/${datasetId}/refreshes`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-type": "application/json",
              },
            }
          );

          const dataExp = await response.json();
          const refreshes = dataExp?.value || [];

          const firstCompletedEntry = refreshes.find(
            (entry) => entry.status === "Completed"
          );

          if (firstCompletedEntry) {
            const utcTime = new Date(firstCompletedEntry.endTime).getTime();

            if (utcTime > (lastRefreshTimestamp || 0)) {
              const endTimeIST = new Date(utcTime).toLocaleString("en-IN", {
                timeZone: "Asia/Kolkata",
              });

              setLastRefreshTimestamp(utcTime); // for comparison
              setLastRefreshDate(endTimeIST); // for display
              completed = true;
              if (onDone) onDone(true);
            } else {
              // Wait and retry if newer refresh not yet complete
              await new Promise((resolve) => setTimeout(resolve, interval));
              attempts++;
            }
          } else {
            await new Promise((resolve) => setTimeout(resolve, interval));
            attempts++;
          }
        } catch (err) {
          console.error("Error polling refresh status", err);
          if (onDone) onDone(false);
          break;
        }
      }

      if (!completed && onDone) {
        console.warn("Refresh not completed after max attempts");
        onDone(false);
      }
    },
    [lastRefreshTimestamp, accessToken, groupId, datasetId]
  );

  const triggerRefresh1 = async () => {
    try {
      const res = await axios.get(
        process.env.NODE_ENV === "development"
          ? `http://localhost:9000/mrunner_api/triggerSP`
          : `${window.location.protocol}//${window.location.host}/mrunner_api/triggerSP`
      );
      // console.log("res", res);
      if (res?.data?.[0]?.get_mrunner_data === "SUCCESS") {
        const response = await fetch(
          `https://api.powerbi.com/v1.0/myorg/groups/${groupId}/datasets/${datasetId}/refreshes`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          console.log("Refresh triggered successfully");
          waitForRefreshCompletion((success) => {
            if (success) {
              console.log("Refresh completed!");
            } else {
              console.log("Refresh did not complete in time");
            }
            refreshDashboard();
            setDateLoaded(true);
          });
        } else {
          const error = await response.json();
          console.error("Error triggering refresh", error);
        }
      } else {
        alert("SP execution failed!");
      }
    } catch (err) {
      console.error("Network or fetch error", err);
    }
  };

  const triggerRefresh = async () => {
    try {
      setDateLoaded(false);
      const res = await axios.post(
        process.env.NODE_ENV === "development"
          ? `http://localhost:9000/mrunner_api/refreshDashboards`
          : `${window.location.protocol}//${window.location.host}/mrunner_api/refreshDashboards`,
        { r_id: reportId }
      );
      // console.log("res", res);
      if (res?.data?.results?.length > 0) {
        const isFound = res.data.results.find(
          (r) => Number(r.report_key) === Number(reportId)
        );
        if (isFound) {
          setLastRefreshDate(isFound.lastRefreshTimestamp); // for display
          setDateLoaded(true);
          refreshDashboard();
        }
      } else {
        alert("Dashboard refresh failed!");
      }
    } catch (err) {
      console.error("Network or fetch error", err);
      alert("Dashboard refresh failed!");
    }
  };

  useEffect(() => {
    waitForRefreshCompletion((success) => {
      if (!success) {
        console.warn("Initial refresh status check failed or incomplete");
      }
      setDateLoaded(true);
    });
  }, []);

  return (
    <div
      className="text-right"
      style={{
        padding: "5px",
        display: "flex",
        justifyContent: "end",
        alignItems: "center",
      }}
    >
      <span style={{ fontSize: "12px" }}>
        Last Refresh Date:{" "}
        {dateLoaded ? (
          lastRefreshDate || "NA"
        ) : (
          <CircularLoader small className="mt-0 mb-0" />
        )}
      </span>
      {dateLoaded && (
        <Tooltip content="Refresh">
          <Button
            small
            type="button"
            value="default"
            className="ml-10px"
            onClick={triggerRefresh}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="16px"
              viewBox="0 0 24 24"
              width="16px"
              fill="#000000"
            >
              <path d="M0 0h24v24H0z" fill="none" />
              <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
            </svg>
          </Button>
        </Tooltip>
      )}
    </div>
  );
};

export default DashboardRefresh;
